import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  ScrollView,
  Switch,
  TouchableOpacity,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../utils/colors';
import { typography } from '../utils/typography';
import { spacing } from '../utils/theme';

interface NotificationSettings {
  enablePush: boolean;
  likes: boolean;
  reposts: boolean;
  follows: boolean;
  mentions: boolean;
  replies: boolean;
  quotes: boolean;
  directMessages: boolean;
}

export const NotificationSettingsScreen: React.FC = () => {
  const navigation = useNavigation();
  const [settings, setSettings] = useState<NotificationSettings>({
    enablePush: false,
    likes: true,
    reposts: true,
    follows: true,
    mentions: true,
    replies: true,
    quotes: true,
    directMessages: true,
  });

  const toggleSetting = (key: keyof NotificationSettings) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>通知設定</Text>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView>
        {/* プッシュ通知セクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>プッシュ通知</Text>
          <Text style={styles.sectionDescription}>
            今後のアップデートで対応予定です
          </Text>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>プッシュ通知を有効化</Text>
              <Text style={styles.settingDescription}>
                アプリを開いていない時も通知を受け取ります
              </Text>
            </View>
            <Switch
              value={settings.enablePush}
              onValueChange={() => toggleSetting('enablePush')}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={settings.enablePush ? colors.primary : colors.textSecondary}
              disabled={true}
            />
          </View>
        </View>

        {/* 通知タイプセクション */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>通知する内容</Text>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>いいね</Text>
              <Text style={styles.settingDescription}>
                投稿にいいねされた時に通知
              </Text>
            </View>
            <Switch
              value={settings.likes}
              onValueChange={() => toggleSetting('likes')}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={settings.likes ? colors.primary : colors.textSecondary}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>リポスト</Text>
              <Text style={styles.settingDescription}>
                投稿がリポストされた時に通知
              </Text>
            </View>
            <Switch
              value={settings.reposts}
              onValueChange={() => toggleSetting('reposts')}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={settings.reposts ? colors.primary : colors.textSecondary}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>新しいフォロワー</Text>
              <Text style={styles.settingDescription}>
                フォローされた時に通知
              </Text>
            </View>
            <Switch
              value={settings.follows}
              onValueChange={() => toggleSetting('follows')}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={settings.follows ? colors.primary : colors.textSecondary}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>メンション</Text>
              <Text style={styles.settingDescription}>
                メンションされた時に通知
              </Text>
            </View>
            <Switch
              value={settings.mentions}
              onValueChange={() => toggleSetting('mentions')}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={settings.mentions ? colors.primary : colors.textSecondary}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>リプライ</Text>
              <Text style={styles.settingDescription}>
                投稿に返信された時に通知
              </Text>
            </View>
            <Switch
              value={settings.replies}
              onValueChange={() => toggleSetting('replies')}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={settings.replies ? colors.primary : colors.textSecondary}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>引用投稿</Text>
              <Text style={styles.settingDescription}>
                投稿が引用された時に通知
              </Text>
            </View>
            <Switch
              value={settings.quotes}
              onValueChange={() => toggleSetting('quotes')}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={settings.quotes ? colors.primary : colors.textSecondary}
            />
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingLabel}>ダイレクトメッセージ</Text>
              <Text style={styles.settingDescription}>
                DMを受信した時に通知（今後対応予定）
              </Text>
            </View>
            <Switch
              value={settings.directMessages}
              onValueChange={() => toggleSetting('directMessages')}
              trackColor={{ false: colors.border, true: colors.primaryLight }}
              thumbColor={settings.directMessages ? colors.primary : colors.textSecondary}
              disabled={true}
            />
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  backButton: {
    padding: 4,
    width: 32,
  },
  headerTitle: {
    ...typography.h2,
    color: colors.text,
    flex: 1,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 32,
  },
  section: {
    marginTop: spacing.lg,
    paddingHorizontal: spacing.md,
  },
  sectionTitle: {
    ...typography.h4,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  sectionDescription: {
    ...typography.caption,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  settingInfo: {
    flex: 1,
    marginRight: spacing.md,
  },
  settingLabel: {
    ...typography.body,
    color: colors.text,
    marginBottom: 4,
  },
  settingDescription: {
    ...typography.caption,
    color: colors.textSecondary,
  },
});
